import { type FC } from "react";
import React from "react";

import { cn, openExternalUrlViaBridge } from "src/utils";
import { MOBILE_MEDIA_QUERY, TABLET_MEDIA_QUERY } from "./constants";

type BannerProps = Readonly<{
    desktopSrc: string;
    tabletSrc: string;
    mobileSrc: string;
    altText: string;
    linkUrl?: string;
    className?: string;
}>;

export const BannerBase: FC<BannerProps> = ({ linkUrl, ...restProps }) => {
    // 1. Banner without link - return just the picture.
    if (!linkUrl) {
        return <BannerPicture {...restProps} />;
    }

    const isExternal = checkIsExternal(linkUrl);

    // 2. If the linkUrl targets our domain, open the link in the same window/tab - no problem in mobile apps.
    if (!isExternal) {
        return (
            <a href={linkUrl}>
                <BannerPicture {...restProps} linkUrl={linkUrl} />
            </a>
        );
    }

    // 3. External link - we need to use kmpJsBridge if it is available. Otherwise, let the browser handle the link.
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const isHandledByKmp = openExternalUrlViaBridge(linkUrl);
        if (isHandledByKmp) {
            e.preventDefault();
        }
    };

    // 4. The handleClick will take care about mobile apps, for classic web, it works as usual
    return (
        <a href={linkUrl} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
            <BannerPicture {...restProps} linkUrl={linkUrl} />
        </a>
    );
};

const BannerPicture: FC<BannerProps> = ({ desktopSrc, tabletSrc, mobileSrc, linkUrl, altText }) => (
    <picture className="flex justify-center">
        <source media={MOBILE_MEDIA_QUERY} srcSet={mobileSrc} />
        <source media={TABLET_MEDIA_QUERY} srcSet={tabletSrc} />
        <img className={cn("rounded-lg", linkUrl && "cursor-pointer")} src={desktopSrc} alt={altText} />
    </picture>
);

const checkIsExternal = (linkUrl: string): boolean => {
    try {
        const url = new URL(linkUrl, window.location.origin);
        return url.hostname !== window.location.hostname;
    } catch (error) {
        return false;
    }
};
